// Password validation utilities for ZoningPal authentication
// Implements professional-grade password requirements

export interface PasswordStrength {
  score: number; // 0-4 (0=very weak, 4=very strong)
  level: 'very-weak' | 'weak' | 'fair' | 'good' | 'strong';
  feedback: string[];
  isValid: boolean;
}

export interface PasswordRequirement {
  met: boolean;
  text: string;
}

export interface PasswordValidationResult {
  requirements: PasswordRequirement[];
  strength: PasswordStrength;
  isValid: boolean;
  errors: string[];
}

// Common weak passwords to reject
const COMMON_PASSWORDS = [
  'password', 'password123', '123456', '123456789', 'qwerty', 'abc123',
  'password1', 'admin', 'letmein', 'welcome', 'monkey', '1234567890',
  'dragon', 'master', 'shadow', 'superman', 'batman', 'hello', 'freedom',
  'whatever', 'qwerty123', 'password12', 'iloveyou', 'princess', 'admin123',
  'welcome123', 'login', 'passw0rd', 'football', 'baseball', 'trustno1',
  'zoning', 'zoningpal', 'toronto', 'development', 'property', 'realtor'
];

// Check if password meets individual requirements
export const checkPasswordRequirements = (password: string): PasswordRequirement[] => {
  const requirements: PasswordRequirement[] = [
    {
      met: password.length >= 8,
      text: 'At least 8 characters long'
    },
    {
      met: /[a-z]/.test(password),
      text: 'Contains lowercase letter (a-z)'
    },
    {
      met: /[A-Z]/.test(password),
      text: 'Contains uppercase letter (A-Z)'
    },
    {
      met: /\d/.test(password),
      text: 'Contains number (0-9)'
    },
    {
      met: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\\/`~]/.test(password),
      text: 'Contains special character (!@#$%^&* etc.)'
    },
    {
      met: !COMMON_PASSWORDS.includes(password.toLowerCase()),
      text: 'Not a commonly used password'
    }
  ];

  return requirements;
};

// Calculate password strength score
export const calculatePasswordStrength = (password: string): PasswordStrength => {
  if (!password) {
    return {
      score: 0,
      level: 'very-weak',
      feedback: ['Enter a password'],
      isValid: false
    };
  }

  const requirements = checkPasswordRequirements(password);
  const metCount = requirements.filter(req => req.met).length;
  const feedback: string[] = [];

  // Base score from requirements
  let score = Math.min(metCount - 1, 4); // 0-4 scale
  
  // Additional scoring factors
  const bonusFactors = {
    length: password.length >= 12 ? 0.5 : 0,
    mixedCase: /[a-z]/.test(password) && /[A-Z]/.test(password) ? 0.2 : 0,
    numbers: (password.match(/\d/g) || []).length >= 2 ? 0.2 : 0,
    symbols: (password.match(/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\\/`~]/g) || []).length >= 2 ? 0.3 : 0,
    variety: new Set(password.toLowerCase()).size / password.length > 0.6 ? 0.3 : 0
  };

  const bonusScore = Object.values(bonusFactors).reduce((sum, bonus) => sum + bonus, 0);
  score = Math.min(score + bonusScore, 4);

  // Determine level and feedback
  let level: PasswordStrength['level'];
  if (score >= 3.5) {
    level = 'strong';
    feedback.push('Excellent! Very strong password.');
  } else if (score >= 2.5) {
    level = 'good';
    feedback.push('Good password strength.');
    if (password.length < 12) feedback.push('Consider making it longer for even better security.');
  } else if (score >= 1.5) {
    level = 'fair';
    feedback.push('Fair password, but could be stronger.');
    if (!requirements[1].met) feedback.push('Add lowercase letters.');
    if (!requirements[2].met) feedback.push('Add uppercase letters.');
    if (!requirements[3].met) feedback.push('Add numbers.');
    if (!requirements[4].met) feedback.push('Add special characters.');
  } else if (score >= 0.5) {
    level = 'weak';
    feedback.push('Weak password. Please strengthen it.');
    feedback.push('Use a mix of uppercase, lowercase, numbers, and symbols.');
  } else {
    level = 'very-weak';
    feedback.push('Very weak password. Please create a stronger one.');
    feedback.push('Must be at least 8 characters with mixed characters.');
  }

  // Check for common patterns
  if (/(.)\1{2,}/.test(password)) {
    feedback.push('Avoid repeating characters (e.g., aaa, 111).');
    score = Math.max(score - 0.5, 0);
  }

  if (/123|abc|qwe|asd|zxc/i.test(password)) {
    feedback.push('Avoid sequential patterns (e.g., 123, abc).');
    score = Math.max(score - 0.5, 0);
  }

  return {
    score: Math.round(score * 10) / 10, // Round to 1 decimal
    level,
    feedback,
    isValid: score >= 2.5 && requirements.slice(0, 5).every(req => req.met) // Must meet basic requirements
  };
};

// Main validation function
export const validatePassword = (password: string): PasswordValidationResult => {
  const requirements = checkPasswordRequirements(password);
  const strength = calculatePasswordStrength(password);
  const errors: string[] = [];

  // Collect unmet requirements as errors
  requirements.forEach(req => {
    if (!req.met) {
      errors.push(req.text);
    }
  });

  // Additional validation errors
  if (password.length > 128) {
    errors.push('Password must be less than 128 characters');
  }

  if (password.includes(' ')) {
    errors.push('Password cannot contain spaces');
  }

  const isValid = errors.length === 0 && strength.isValid;

  return {
    requirements,
    strength,
    isValid,
    errors
  };
};

// Quick validation for form submission
export const isPasswordValid = (password: string): boolean => {
  return validatePassword(password).isValid;
};

// Get user-friendly error message for form display
export const getPasswordError = (password: string): string | null => {
  if (!password) return 'Password is required';
  
  const validation = validatePassword(password);
  if (validation.isValid) return null;
  
  // Return the most important error
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!validation.requirements[1].met) return 'Password must contain lowercase letters';
  if (!validation.requirements[2].met) return 'Password must contain uppercase letters';  
  if (!validation.requirements[3].met) return 'Password must contain numbers';
  if (!validation.requirements[4].met) return 'Password must contain special characters';
  if (!validation.requirements[5].met) return 'Please choose a less common password';
  
  return 'Password does not meet security requirements';
}; 